import { useState } from 'react';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useT } from '../../i18n/LanguageContext';
import { BRAND } from '../../data/site';
import { Lock, User, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';

export default function AdminLogin() {
  const { login, isAuthed } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthed) {
    const to = location.state?.from?.pathname || '/admin';
    return <Navigate to={to} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter both username and password');
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      toast.success('Welcome back.');
      const to = location.state?.from?.pathname || '/admin';
      navigate(to, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid credentials';
      toast.error(typeof msg === 'string' ? msg : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="min-h-screen bg-base flex items-center justify-center p-6">
      <Toaster theme="dark" position="top-right" richColors />
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" data-testid="admin-login-home-logo" title="Back to MBK Holding">
            <img src={BRAND.logo} alt="MBK" className="h-16 w-auto mx-auto transition-transform hover:scale-105" />
          </Link>
          <p className="font-mono text-[10px] tracking-[0.3em] text-brand-teal-light uppercase mt-4">Internal · Secure</p>
          <h1 className="font-display text-3xl text-prim mt-2 tracking-tight">{t('admin.login')}</h1>
        </div>

        <form onSubmit={onSubmit} className="surface-2 border border-subtle rounded-lg p-8 shimmer-border" noValidate>
          <div className="space-y-5 surface-2 -m-1 p-1">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">
                {t('admin.username')}
              </label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                <input
                  type="text"
                  data-testid="admin-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="lux-input"
                  style={{ paddingLeft: '2.5rem' }}
                  placeholder="Supermbk"
                  autoFocus
                  autoComplete="username"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-muted mb-2">
                {t('admin.password')}
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                <input
                  type="password"
                  data-testid="admin-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="lux-input"
                  style={{ paddingLeft: '2.5rem' }}
                  placeholder="••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="admin-signin"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> …</> : t('admin.signIn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
